import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { API_CONSTANTS } from "../../../API_CONSTANTS";
import Toast from "../../../Components/Toast/Toast";
import Spinner from "react-bootstrap/Spinner";
import toArrayBuffer from "to-array-buffer";
import {
  faCheckCircle,
  faTimesCircle,
} from "@fortawesome/fontawesome-free-solid";

export default function EditSurgeryModal({ data, onSurgeryUpdated, show, onHide }) {
  const [isLoading, setIsLoading] = useState(false);
  const [ToastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState("");
  const [ToastIcon, setToastIcon] = useState(faTimesCircle);
  const [iconColor, setIconColor] = useState("");
  const [procedureImageFile, setProcedureImageFile] = useState(null);
  const [doctorImageFile, setDoctorImageFile] = useState(null);
  const [uploadingProcedureImage, setUploadingProcedureImage] = useState(false);
  const [uploadingDoctorImage, setUploadingDoctorImage] = useState(false);
  const [doctorsList, setDoctorsList] = useState([]);
  const [selectedDoctors, setSelectedDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [doctorsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [hasMoreDoctors, setHasMoreDoctors] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Helper to coerce PMDC values into strict boolean
  const parseBool = (v) => {
    return v === true || v === 'true' || v === 1 || v === '1';
  };

  const fetchDoctors = async (cursor = null) => {
    const isLoadingMore = cursor !== null;
    
    if (!isLoadingMore) {
      setLoadingDoctors(true);
    } else {
      setIsLoadingMore(true);
    }
    
    try {
      let apiUrl = API_CONSTANTS.DOCTOR_LIST_SEARCH();
      let requestBody = {};

      if (cursor) {
        apiUrl = `${API_CONSTANTS.DOCTOR_LIST_SEARCH()}?last=${encodeURIComponent(cursor)}`;
      }
      
      const response = await axios.post(
        apiUrl,
        requestBody,
        {
          headers: API_CONSTANTS.headers(),
          timeout: 10000,
        }
      );

      const items = response.data.response?.Items || [];
      const newLastEvaluatedKey = response.data.response?.LastEvaluatedKey || null;

      // Append new doctors if loading more, otherwise replace
      if (cursor && cursor !== null && items.length > 0) {
        setDoctorsList(prevList => {
          const newList = [...prevList, ...items];
          
          // Filter out duplicates by Id
          const seenIds = new Set();
          const uniqueDoctors = newList.filter(doctor => {
            const id = doctor.Id || doctor.id;
            if (seenIds.has(id)) {
              return false; // Skip this duplicate
            }
            seenIds.add(id);
            return true;
          });
          return uniqueDoctors;
        });
      } else if (!cursor) {
        setDoctorsList(items);
      }

      setLastEvaluatedKey(newLastEvaluatedKey);
      setHasMoreDoctors(!!newLastEvaluatedKey);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || "Error fetching doctors";
      handleToast(errorMsg, faTimesCircle, "#dc3545", "show-error");
    } finally {
      if (!isLoadingMore) {
        setLoadingDoctors(false);
      } else {
        setIsLoadingMore(false);
      }
    }
  };

  const loadMoreDoctors = () => {
    if (lastEvaluatedKey && !isLoadingMore) {
      setIsLoadingMore(true);
      fetchDoctors(lastEvaluatedKey);
    }
  };

  const getFilteredAndPaginatedDoctors = () => {
    const filteredDoctors = doctorsList.filter(doctor => {
      const doctorName = (doctor.Fname || doctor.firstName || doctor.first_name || doctor.name || '').toLowerCase();
      const doctorEmail = (doctor.email || '').toLowerCase();
      return doctorName.includes(searchTerm.toLowerCase()) || doctorEmail.includes(searchTerm.toLowerCase());
    });
    
    const totalPages = Math.ceil(filteredDoctors.length / doctorsPerPage);
    const paginatedDoctors = filteredDoctors.slice((currentPage - 1) * doctorsPerPage, currentPage * doctorsPerPage);
    
    return { paginatedDoctors, totalPages, totalAvailable: filteredDoctors.length, currentPage };
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = (totalPages) => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  useEffect(() => {
    if (show) {
      // Reset all state when modal opens
      setSelectedDoctors([]);
      setCurrentPage(1);
      setSearchTerm("");
      setDoctorsList([]);
      setLastEvaluatedKey(null);
      setHasMoreDoctors(true);
      setIsLoadingMore(false);
      fetchDoctors();
    } else {
      // Reset all state when modal closes
      setSelectedDoctors([]);
      setCurrentPage(1);
      setSearchTerm("");
      setDoctorsList([]);
      setLastEvaluatedKey(null);
      setHasMoreDoctors(true);
      setIsLoadingMore(false);
    }
  }, [show]);

  useEffect(() => {
    if (show && data && doctorsList.length > 0) {
      // Reset selected doctors first
      setSelectedDoctors([]);
      setCurrentPage(1);
      setSearchTerm("");
      
      // Handle multiple doctors from the data
      const doctors = data?.doctors || [];
      if (doctors.length > 0) {
        const selected = doctors.map(doctorData => {
          const doctor = doctorsList.find(d => d.Id === doctorData.doctor_id);
          return doctor || null;
        }).filter(Boolean);
        setSelectedDoctors(selected);
      } else {
        // Fallback for old single doctor format
        const currentDoctorId = data?.doctor?.doctor_id;
        if (currentDoctorId) {
          const doctor = doctorsList.find(d => d.Id === currentDoctorId);
          if (doctor) {
            setSelectedDoctors([doctor]);
          }
        }
      }
    }
  }, [show, data, doctorsList]);

  const handleDoctorSelect = async (doctor, setFieldValue, values) => {
    if (!doctor) return;

    // Check if doctor is already selected
    const isAlreadySelected = selectedDoctors.some(d => d.Id === doctor.Id);
    if (isAlreadySelected) {
      handleToast("Doctor already selected", faTimesCircle, "#dc3545", "show-error");
      return;
    }

    try {
      const response = await axios.get(
        `${API_CONSTANTS.BASEURL}admin/doctors/${doctor.Id}`,
        {
          headers: API_CONSTANTS.headers(),
        }
      );
      const doctorData = response.data.response;

      const newDoctor = {
        doctor_id: doctor.Id,
        doctor_name: doctorData.Fname || `${doctorData.personal_info?.firstName || ''} ${doctorData.personal_info?.lastName || ''}`.trim(),
        doctor_specialization: doctorData.specialization || "",
        doctor_image_url: doctorData.personal_info?.profileUrl || "",
        rating: doctorData.rating?.averageRating?.toString() || "",
        experience: doctorData.yearsOfExperience || "",
      };

      const updatedDoctors = [...selectedDoctors, doctor];
      setSelectedDoctors(updatedDoctors);

      // Update form values
      const currentDoctors = values.doctors || [];
      const newDoctorData = {
        doctor_id: doctor.Id,
        doctor_name: doctorData.Fname || `${doctorData.personal_info?.firstName || ''} ${doctorData.personal_info?.lastName || ''}`.trim(),
        doctor_specialization: doctorData.specialization || "",
        doctor_image_url: doctorData.personal_info?.profileUrl || "",
        experience: doctorData.yearsOfExperience || 0,
        pmdc: parseBool(doctorData.pmdc),
      };
      setFieldValue("doctors", [...currentDoctors, newDoctorData]);
    } catch (error) {
      handleToast("Error loading doctor details", faTimesCircle, "#dc3545", "show-error");
    }
  };

  const handleRemoveDoctor = (doctorId, setFieldValue, values) => {
    // Remove from selectedDoctors state (for dropdown filtering)
    const updatedSelected = selectedDoctors.filter(d => d.Id !== doctorId);
    setSelectedDoctors(updatedSelected);
    // Remove from Formik values
    const updatedDoctorData = (values.doctors || []).filter(doctor => doctor.doctor_id !== doctorId);
    setFieldValue("doctors", updatedDoctorData);
  };

  const handleToast = (message, icon, iconColor, className) => {
    setToastIcon(icon);
    setToastMessage(message);
    setShowToast(`show ${className}`);
    setIconColor(iconColor);
    setTimeout(() => setShowToast(""), 2900);
  };

  const uploadImage = async (file) => {
    const reader = new FileReader();
    reader.readAsBinaryString(file);
    return new Promise((resolve, reject) => {
      reader.onload = async () => {
        const fileRes = btoa(reader.result);
        const testImg = toArrayBuffer(fileRes);
        const data = testImg;
        try {
          const response = await axios.post(
            `${API_CONSTANTS.BASEURL}admin/image-upload-api/user/a80422ab-1f3c-4676-8200-fc38a156599f/type/new`,
            data,
            {
              headers: API_CONSTANTS.imgheaders(),
            }
          );
          resolve(response.data.response.url);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error("File reading error"));
    });
  };

  const handleProcedureImageChange = async (event, setFieldValue) => {
    const file = event.target.files[0];
    if (file) {
      setProcedureImageFile(file);
      setUploadingProcedureImage(true);
      try {
        const url = await uploadImage(file);
        setFieldValue("procedure_image_url", url);
        handleToast("Procedure image uploaded successfully", faCheckCircle, "#009b94", "show-success");
      } catch (error) {
        handleToast("Error uploading procedure image", faTimesCircle, "#dc3545", "show-error");
      } finally {
        setUploadingProcedureImage(false);
      }
    }
  };

  const handleDoctorImageChange = async (event, setFieldValue) => {
    const file = event.target.files[0];
    if (file) {
      setDoctorImageFile(file);
      setUploadingDoctorImage(true);
      try {
        const url = await uploadImage(file);
        setFieldValue("doctor_image_url", url);
        handleToast("Doctor image uploaded successfully", faCheckCircle, "#009b94", "show-success");
      } catch (error) {
        handleToast("Error uploading doctor image", faTimesCircle, "#dc3545", "show-error");
      } finally {
        setUploadingDoctorImage(false);
      }
    }
  };

  const initialValues = React.useMemo(() => {
    // Ensure data is valid
    const safeData = data || {};

    // Ensure doctors is always an array
    let doctorsArray = [];
    if (Array.isArray(safeData.doctors) && safeData.doctors.length > 0) {
      doctorsArray = safeData.doctors.map(doctor => ({
        doctor_id: doctor.doctor_id,
        doctor_name: doctor.doctor_name || doctor.name || '',
        doctor_specialization: doctor.doctor_specialization || doctor.specialization || '',
        doctor_image_url: doctor.doctor_image_url || doctor.image_url || '',
        experience: doctor.experience || 0,
        clinic_name: doctor.clinic?.name || doctor.clinic_name || '',
        clinic_address: doctor.clinic?.address || doctor.clinic_address || '',
        city: doctor.clinic?.city || doctor.city || '',
        duration: doctor.duration || '',
        pricing: doctor.pricing || '',
        currency: doctor.currency || '',
      }));
    } else if (safeData.doctor) {
      doctorsArray = [{
        doctor_id: safeData.doctor.doctor_id,
        doctor_name: safeData.doctor.name || '',
        doctor_specialization: safeData.doctor.specialization || '',
        doctor_image_url: safeData.doctor.image_url || '',
        experience: safeData.doctor.experience || 0,
        clinic_name: safeData.doctor.clinic?.name || '',
        clinic_address: safeData.doctor.clinic?.address || '',
        city: safeData.doctor.clinic?.city || '',
        pmdc: parseBool(safeData.doctor.pmdc),
      }];
    }



    const values = {
      procedure_name: safeData.procedure_name || "",
      procedure_type: safeData.procedure_type || "surgery",
      description: safeData.description || "",
      procedure_image_url: safeData.procedure_image_url || "",
      doctors: doctorsArray,
      clinic_name: doctorsArray[0]?.clinic_name || safeData.doctors?.[0]?.clinic_name || safeData.clinic_name || "",
      clinic_address: doctorsArray[0]?.clinic_address || safeData.doctors?.[0]?.clinic_address || safeData.clinic_address || "",
      city: doctorsArray[0]?.city || safeData.doctors?.[0]?.city || safeData.city || "",
      duration: safeData.duration || doctorsArray[0]?.duration || safeData.doctors?.[0]?.duration || "",
      pricing: doctorsArray[0]?.pricing || safeData.doctors?.[0]?.pricing || safeData.pricing || "",
      currency: doctorsArray[0]?.currency || safeData.doctors?.[0]?.currency || safeData.currency || "PKR",
    };
    return values;
  }, [data]);

  const validationSchema = Yup.object({
    procedure_name: Yup.string()
      .required("Procedure name is required")
      .min(3, "Procedure name must be at least 3 characters"),
    procedure_type: Yup.string()
      .required("Procedure type is required")
      .oneOf(["surgery", "procedure"], "Invalid procedure type"),
    description: Yup.string()
      .required("Description is required")
      .min(10, "Description must be at least 10 characters"),
    procedure_image_url: Yup.string()
      .test('is-url-or-empty', 'Invalid URL format', (value) => !value || value.startsWith('http'))
      .nullable(),
    doctors: Yup.array()
      .of(Yup.object())
      .min(1, "At least one doctor must be selected")
      .required("Please select at least one doctor")
      .ensure(),
    clinic_name: Yup.string()
      .required("Clinic name is required")
      .min(3, "Clinic name must be at least 3 characters"),
    clinic_address: Yup.string()
      .required("Clinic address is required")
      .min(10, "Clinic address must be at least 10 characters"),
    city: Yup.string()
      .required("City is required")
      .min(2, "City must be at least 2 characters"),
    duration: Yup.string()
      .required("Duration is required")
      .min(1, "Duration must be specified"),
    pricing: Yup.number()
      .required("Pricing is required")
      .positive("Pricing must be a positive number"),
    currency: Yup.string()
      .required("Currency is required")
      .oneOf(["PKR", "USD", "EUR"], "Invalid currency"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    setIsLoading(true);

    // Check if doctors array has at least one doctor
    if (!values.doctors || values.doctors.length === 0) {
      handleToast("Please select at least one doctor", faTimesCircle, "#dc3545", "show-error");
      setIsLoading(false);
      setSubmitting(false);
      return;
    }

    // Validate required fields
    if (!values.clinic_name?.trim()) {
      handleToast("Clinic name is required", faTimesCircle, "#dc3545", "show-error");
      setIsLoading(false);
      setSubmitting(false);
      return;
    }

    // Validate pricing
    const pricing = parseFloat(values.pricing);
    if (isNaN(pricing) || pricing < 0) {
      handleToast("Please enter a valid price", faTimesCircle, "#dc3545", "show-error");
      setIsLoading(false);
      setSubmitting(false);
      return;
    }

    const submitData = {
      procedure_id: data.id,
      procedure_name: values.procedure_name?.trim(),
      procedure_type: values.procedure_type,
      description: values.description?.trim(),
      procedure_image_url: values.procedure_image_url || "https://via.placeholder.com/300x200.png?text=Procedure+Image",
      duration: values.duration?.trim(),
      // Send full doctor details for each doctor WITH all required fields
      doctors: values.doctors.map(doctor => ({
        doctor_id: doctor.doctor_id,
        doctor_name: doctor.doctor_name?.trim(),
        doctor_specialization: doctor.doctor_specialization?.trim(),
        doctor_image_url: doctor.doctor_image_url || "https://via.placeholder.com/150x150.png?text=No+Image",
        pmdc: parseBool(doctor.pmdc),
        clinic_name: values.clinic_name?.trim(),
        clinic_address: values.clinic_address?.trim(),
        city: values.city?.trim(),
        pricing: values.pricing ? String(values.pricing) : "",
        currency: values.currency,
        experience: parseInt(doctor.experience) || 0,
      })),
    };

    axios
      .post(`${API_CONSTANTS.EDIT_PROCEDURE_AND_SURGERY()}`, submitData, {
        headers: API_CONSTANTS.headers(),
        timeout: 30000, // 30 second timeout
      })
      .then((response) => {
        // Accept various success indicators
        const isSuccess = response.status === 200 || 
                         response.status === 201 ||
                         response.data?.response?.message === "Procedure/Surgery updated successfully" ||
                         response.data?.message === "Success" ||
                         response.data?.message === "Procedure/Surgery updated successfully";
        
        if (isSuccess) {
          handleToast(
            "Procedure/Surgery Updated Successfully",
            faCheckCircle,
            "#009b94",
            "show-success"
          );
          onHide();
          if (onSurgeryUpdated) {
            onSurgeryUpdated();
          }
        } else {
          handleToast("Unexpected response from server", faTimesCircle, "#dc3545", "show-error");
        }
      })
      .catch((error) => {
        let errorMessage = "Error updating procedure/surgery";
        
        if (error.code === "ERR_NETWORK") {
          errorMessage = "❌ Network Error: Cannot reach the API endpoint. Is the backend running? Check: " + (error.config?.url || "unknown URL");
        } else if (error.response?.status === 404) {
          errorMessage = "API endpoint not found (404). Please verify the backend route exists.";
        } else if (error.response?.status === 400) {
          errorMessage = "Validation Error: " + (error.response?.data?.message || error.response?.data?.response?.message || "Invalid request data");
        } else if (error.response?.status === 401 || error.response?.status === 403) {
          errorMessage = "Authentication Error: Please check your authorization token.";
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.data?.response?.message) {
          errorMessage = error.response.data.response.message;
        }
        
        handleToast(
          errorMessage,
          faTimesCircle,
          "#dc3545",
          "show-error"
        );
      })
      .finally(() => {
        setIsLoading(false);
        setSubmitting(false);
      });
  };

  return (
    <>
      <style>{`
        .doctor-list-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .doctor-list-scroll::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        .doctor-list-scroll::-webkit-scrollbar-thumb {
          background: #009b94;
          border-radius: 4px;
        }
        .doctor-list-scroll::-webkit-scrollbar-thumb:hover {
          background: #007d75;
        }
      `}</style>
      <Modal show={show} size="lg" centered backdrop="static" onHide={onHide} style={{ zIndex: 9999 }}>
        <Modal.Header closeButton onHide={onHide}>
          <Modal.Title>Edit Procedure/Surgery</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column">
          {!data ? (
            <div className="d-flex justify-content-center align-items-center p-5">
              <Spinner animation="border" role="status" />
            </div>
          ) : (
          <Formik
            key={data?.id || 'new'}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ setFieldValue, values, isSubmitting }) => (
              <Form className="form-inline row g-3">
                <div className="col-md-6 mb-4">
                  <Field
                    type="text"
                    name="procedure_name"
                    placeholder="Procedure Name"
                    className="form-control border-0 border-bottom"
                  />
                  <ErrorMessage name="procedure_name" component="div" className="error-message" />
                </div>
                <div className="col-md-6 mb-4">
                  <Field
                    as="select"
                    name="procedure_type"
                    className="form-control border-0 border-bottom"
                  >
                    <option value="">Select Type</option>
                    <option value="surgery">Surgery</option>
                    <option value="procedure">Procedure</option>
                  </Field>
                  <ErrorMessage name="procedure_type" component="div" className="error-message" />
                </div>
                <div className="col-md-12 mb-4">
                  <Field
                    as="textarea"
                    name="description"
                    placeholder="Description"
                    className="form-control border-0 border-bottom"
                    rows="4"
                  />
                  <ErrorMessage
                    name="description"
                    component="div"
                    className="error-message"
                  />
                </div>
                <div className="col-md-12 mb-4">
                  <label htmlFor="procedure_image" className="form-label">Procedure Image</label>
                  <input
                    type="file"
                    id="procedure_image"
                    accept="image/*"
                    onChange={(e) => handleProcedureImageChange(e, setFieldValue)}
                    className="form-control"
                    disabled={uploadingProcedureImage}
                  />
                  {uploadingProcedureImage && <div>Uploading...</div>}
                  <Field
                    type="url"
                    name="procedure_image_url"
                    placeholder="Procedure Image URL"
                    className="form-control border-0 border-bottom mt-2"
                    readOnly
                  />
                  <ErrorMessage
                    name="procedure_image_url"
                    component="div"
                    className="error-message"
                  />
                </div>
                <div className="col-md-12 mb-4">
                  <label className="form-label fw-bold">Select Doctors</label>
                  {loadingDoctors && (
                    <div className="alert alert-info mb-3" style={{ padding: '8px 12px' }}>
                      <strong> Loading doctors...</strong> Fetching from database
                    </div>
                  )}
                  {!loadingDoctors && doctorsList.length === 0 && (
                    <div className="alert alert-warning mb-3" style={{ padding: '8px 12px' }}>
                      <strong> No doctors found</strong> 
                      <br />
                      <small>Verify doctors exist in the database</small>
                    </div>
                  )}

                  {/* SEARCH BOX */}
                  {!loadingDoctors && doctorsList.length > 0 && (
                    <div className="mb-3">
                      <input
                        type="text"
                        placeholder="Search doctors by name..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="form-control border-0 border-bottom"
                        style={{ marginBottom: '10px' }}
                      />
                    </div>
                  )}

                  {/* PAGINATION DOCTOR LIST */}
                  {!loadingDoctors && doctorsList.length > 0 && (
                    (() => {
                      const { paginatedDoctors, totalPages, totalAvailable } = getFilteredAndPaginatedDoctors();

                      return (
                        <div style={{
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          padding: '0',
                          backgroundColor: '#f9f9f9',
                          marginBottom: '15px'
                        }}>
                          {/* DOCTOR LIST - SCROLLABLE */}
                          <div style={{
                            maxHeight: '350px',
                            overflowY: 'auto',
                            paddingRight: '8px',
                            paddingLeft: '12px'
                          }} className="doctor-list-scroll">
                            {paginatedDoctors.length > 0 ? (
                              paginatedDoctors.map((doctor) => {
                                const doctorId = doctor.Id || doctor.id || doctor.doctor_id;
                                const doctorName = doctor.Fname || doctor.firstName || doctor.first_name || doctor.name || 'Unknown Doctor';
                                const specialization = doctor.specialization || 'No specialization';

                                return (
                                  <div
                                    key={doctorId}
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      padding: '12px 8px',
                                      borderBottom: '1px solid #e0e0e0',
                                      backgroundColor: '#fff',
                                      marginBottom: '4px',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s',
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#e8f5e9';
                                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = '#fff';
                                      e.currentTarget.style.boxShadow = 'none';
                                    }}
                                  >
                                    <div style={{ flex: 1 }}>
                                      <strong style={{ color: '#009b94', fontSize: '14px' }}>{doctorName}</strong>
                                      <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                        {specialization}
                                      </div>
                                      {doctor.email && (
                                        <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                                          📧 {doctor.email}
                                        </div>
                                      )}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        handleDoctorSelect(doctor, setFieldValue, values);
                                      }}
                                      style={{
                                        backgroundColor: '#009b94',
                                        color: '#fff',
                                        border: 'none',
                                        padding: '6px 12px',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        whiteSpace: 'nowrap',
                                        marginLeft: '10px',
                                        transition: 'all 0.2s'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = '#007d75';
                                        e.target.style.transform = 'scale(1.05)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = '#009b94';
                                        e.target.style.transform = 'scale(1)';
                                      }}
                                    >
                                      + Select
                                    </button>
                                  </div>
                                );
                              })
                            ) : (
                              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                                {searchTerm ? ' No doctors match your search' : ' No doctors available'}
                              </div>
                            )}
                          </div>

                          {/* PAGINATION INFO */}
                          <div style={{
                            padding: '12px',
                            borderTop: '1px solid #ddd',
                            backgroundColor: '#f5f5f5',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '12px',
                          }}>
                            <div style={{ color: '#666' }}>
                              <strong>Showing {paginatedDoctors.length > 0 ? ((currentPage - 1) * doctorsPerPage) + 1 : 0}-{Math.min(currentPage * doctorsPerPage, totalAvailable)}</strong> of <strong>{totalAvailable}</strong> available doctors
                              {searchTerm && <span style={{ color: '#999' }}> (filtered from {doctorsList.length} total)</span>}
                            </div>
                            <div style={{ color: '#666' }}>
                              <strong>Page {currentPage} of {totalPages}</strong>
                            </div>
                          </div>

                          {/* PAGINATION CONTROLS */}
                          {totalPages > 1 && (
                            <div style={{
                              padding: '10px 12px',
                              display: 'flex',
                              gap: '8px',
                              justifyContent: 'center',
                              alignItems: 'center',
                              borderTop: '1px solid #ddd',
                              flexWrap: 'wrap',
                              backgroundColor: '#fff'
                            }}>
                              <button
                                type="button"
                                onClick={handlePrevPage}
                                disabled={currentPage === 1}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: currentPage === 1 ? '#e0e0e0' : '#009b94',
                                  color: currentPage === 1 ? '#999' : '#fff',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                  if (currentPage > 1) {
                                    e.target.style.backgroundColor = '#007d75';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (currentPage > 1) {
                                    e.target.style.backgroundColor = '#009b94';
                                  }
                                }}
                              >
                                ← Prev
                              </button>

                              {/* PAGE NUMBERS */}
                              <div style={{ display: 'flex', gap: '4px' }}>
                                {Array.from({ length: Math.min(totalPages, 5) }).map((_, idx) => {
                                  let pageNum;
                                  if (totalPages <= 5) {
                                    pageNum = idx + 1;
                                  } else if (currentPage <= 3) {
                                    pageNum = idx + 1;
                                  } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - (4 - idx);
                                  } else {
                                    pageNum = currentPage - 2 + idx;
                                  }

                                  return (
                                    <button
                                      key={pageNum}
                                      type="button"
                                      onClick={() => {
                                        setCurrentPage(pageNum);
                                      }}
                                      style={{
                                        padding: '6px 10px',
                                        backgroundColor: currentPage === pageNum ? '#009b94' : '#f0f0f0',
                                        color: currentPage === pageNum ? '#fff' : '#333',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontWeight: currentPage === pageNum ? '700' : '500',
                                        fontSize: '12px',
                                        transition: 'all 0.2s'
                                      }}
                                      onMouseEnter={(e) => {
                                        if (currentPage !== pageNum) {
                                          e.target.style.backgroundColor = '#d0d0d0';
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (currentPage !== pageNum) {
                                          e.target.style.backgroundColor = '#f0f0f0';
                                        }
                                      }}
                                    >
                                      {pageNum}
                                    </button>
                                  );
                                })}
                              </div>

                              <button
                                type="button"
                                onClick={() => handleNextPage(totalPages)}
                                disabled={currentPage === totalPages}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: currentPage === totalPages ? '#e0e0e0' : '#009b94',
                                  color: currentPage === totalPages ? '#999' : '#fff',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                  if (currentPage < totalPages) {
                                    e.target.style.backgroundColor = '#007d75';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (currentPage < totalPages) {
                                    e.target.style.backgroundColor = '#009b94';
                                  }
                                }}
                              >
                                Next →
                              </button>
                            </div>
                          )}

                          {/* LOAD MORE BUTTON */}
                          {!isLoadingMore && hasMoreDoctors && (
                            <div style={{
                              padding: '12px',
                              borderTop: '1px solid #ddd',
                              backgroundColor: '#f0f8f7',
                              textAlign: 'center'
                            }}>
                              <button
                                type="button"
                                onClick={loadMoreDoctors}
                                style={{
                                  padding: '10px 20px',
                                  backgroundColor: '#009b94',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontWeight: '600',
                                  fontSize: '13px',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = '#007d75';
                                  e.target.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = '#009b94';
                                  e.target.style.transform = 'scale(1)';
                                }}
                              >
                                 Load More Doctors
                              </button>
                              <div style={{ fontSize: '11px', color: '#666', marginTop: '8px' }}>
                                Load more doctors from database
                              </div>
                            </div>
                          )}

                          {isLoadingMore && (
                            <div style={{
                              padding: '12px',
                              borderTop: '1px solid #ddd',
                              backgroundColor: '#f0f8f7',
                              textAlign: 'center',
                              color: '#009b94',
                              fontSize: '13px'
                            }}>
                              <Spinner animation="border" size="sm" style={{ marginRight: '8px' }} />
                              Loading more doctors...
                            </div>
                          )}
                        </div>
                      );
                    })()
                  )}
                </div>

                {/* Display doctors from Formik values.doctors (saved in DB) */}
                {(values.doctors || []).length > 0 && (
                  <div className="col-md-12 mb-4">
                    <h6 className="fw-bold">Selected Doctors:</h6>
                    {(values.doctors || []).map((doctor) => (
                      <div key={doctor.doctor_id} className="d-flex align-items-center justify-content-between bg-light p-2 rounded mb-2">
                        <div>
                          <strong>{doctor.doctor_name || "Unknown"}</strong>
                          {doctor.doctor_specialization && (
                            <small className="text-muted ms-2">{doctor.doctor_specialization}</small>
                          )}
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleRemoveDoctor(doctor.doctor_id, setFieldValue, values)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Clinic Information */}
                <div className="col-12 mb-2">
                  <h6 className="fw-bold text-secondary">Clinic Information</h6>
                </div>
                <div className="col-md-6 mb-4">
                  <label className="form-label">Clinic Name</label>
                  <Field
                    type="text"
                    name="clinic_name"
                    placeholder="Clinic Name"
                    className="form-control border-0 border-bottom"
                  />
                  <ErrorMessage name="clinic_name" component="div" className="error-message" />
                </div>
                <div className="col-md-6 mb-4">
                  <label className="form-label">Clinic Address</label>
                  <Field
                    type="text"
                    name="clinic_address"
                    placeholder="Clinic Address"
                    className="form-control border-0 border-bottom"
                  />
                  <ErrorMessage name="clinic_address" component="div" className="error-message" />
                </div>
                <div className="col-md-6 mb-4">
                  <label className="form-label">City</label>
                  <Field
                    type="text"
                    name="city"
                    placeholder="City"
                    className="form-control border-0 border-bottom"
                  />
                  <ErrorMessage name="city" component="div" className="error-message" />
                </div>
                <div className="col-md-6 mb-4">
                  <label className="form-label">Duration <span className="text-danger">*</span></label>
                  <Field
                    type="text"
                    name="duration"
                    placeholder="Duration (e.g., 2 hours)"
                    className="form-control border-0 border-bottom"
                  />
                  <ErrorMessage name="duration" component="div" className="error-message" />
                </div>
                <div className="col-md-6 mb-4">
                  <Field
                    type="number"
                    name="pricing"
                    placeholder="Pricing"
                    className="form-control border-0 border-bottom"
                  />
                  <ErrorMessage name="pricing" component="div" className="error-message" />
                </div>
                <div className="col-md-6 mb-4">
                  <Field
                    as="select"
                    name="currency"
                    className="form-control border-0 border-bottom"
                  >
                    <option value="PKR">PKR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </Field>
                  <ErrorMessage name="currency" component="div" className="error-message" />
                </div>
                <div className="col-12 d-flex justify-content-end gap-2 mt-4">
                  <button
                    type="button"
                    className="btn"
                    style={{
                      border: "1px solid #79747E",
                      color: "#333",
                      background: "none",
                    }}
                    onClick={onHide}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn"
                    style={{ background: "#009B93", color: "#fff" }}
                    disabled={isLoading}
                  >
                    {isLoading && (
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />
                    )}{" "}
                    Update
                  </button>
                </div>
              </Form>
            )}
          </Formik>
          )}
        </Modal.Body>
      </Modal>
      <Toast
        cName={showToast}
        icon={ToastIcon}
        color={iconColor}
        text={ToastMessage}
      />
    </>
  );
}
